import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { WineSummary } from '@/api/wines';
import type { CellarFormData } from '@/components/cellar/AddToCellarModal';

export interface CellarItem {
  wineId: string;
  quantity: number;
  drinkFrom?: string | null;
  drinkBy?: string | null;
  location?: string | null;
  wine: WineSummary;
}

async function fetchCellar(): Promise<CellarItem[]> {
  const { data: entries } = await apiClient.models.CellarEntry.list({ limit: 200 });
  if (!entries || entries.length === 0) return [];

  // Fetch wine details for all cellar entries
  const wineIds = [...new Set(entries.map((e) => e.wineId))];
  const wines = await Promise.all(
    wineIds.map((id) => apiClient.models.Wine.get({ id }))
  );
  const wineMap = new Map(
    wines
      .filter((r) => r.data)
      .map((r) => [r.data!.id, r.data!])
  );

  return entries
    .filter((e) => wineMap.has(e.wineId))
    .map((e) => {
      const w = wineMap.get(e.wineId)!;
      return {
        wineId: e.wineId,
        quantity: e.quantity,
        drinkFrom: e.drinkFrom ?? null,
        drinkBy: e.drinkBy ?? null,
        location: e.location ?? null,
        wine: {
          id: w.id,
          name: w.name,
          producer: w.producer ?? undefined,
          vintage: w.vintage ?? undefined,
          style: w.style ?? undefined,
          region: w.region ?? undefined,
        },
      };
    });
}

export function useCellar() {
  const { data: items = [], isLoading: loading } = useQuery({
    queryKey: ['cellar'],
    queryFn: fetchCellar,
  });

  return { items, loading };
}

export function useAddToCellar() {
  const queryClient = useQueryClient();

  return async (entry: CellarFormData) => {
    // Check if already in cellar — update quantity if so
    const { data: existing } = await apiClient.models.CellarEntry.list({
      filter: { wineId: { eq: entry.wineId } },
      limit: 1,
    });

    if (existing && existing.length > 0) {
      await apiClient.models.CellarEntry.update({
        id: existing[0].id,
        quantity: existing[0].quantity + entry.quantity,
        ...(entry.purchasePrice !== undefined && { purchasePrice: entry.purchasePrice }),
        ...(entry.drinkFrom && { drinkFrom: entry.drinkFrom }),
        ...(entry.drinkBy && { drinkBy: entry.drinkBy }),
        ...(entry.location && { location: entry.location }),
        ...(entry.notes && { notes: entry.notes }),
      });
    } else {
      await apiClient.models.CellarEntry.create({
        wineId: entry.wineId,
        quantity: entry.quantity,
        purchasePrice: entry.purchasePrice,
        purchaseDate: entry.purchaseDate,
        drinkFrom: entry.drinkFrom,
        drinkBy: entry.drinkBy,
        location: entry.location,
        notes: entry.notes,
      });
    }

    queryClient.invalidateQueries({ queryKey: ['cellar'] });
  };
}
